import { DateInput, EnumDropdown, TextInput, useFormContext, WysiwygInput } from 'framework/form';
import React, { useState } from 'react';
import { Grid2 as Grid, Stack } from '@mui/material';
import { CLIENT_PORTAL_TASK_TYPE_LABELS, CLIENT_PORTAL_TASK_TYPES } from 'domain/shared/clientPortal';
import { AttachmentUploaderInput } from 'domain/shared/artifact/input';
import { useDateService } from 'framework/utils';
import { getClientPortalAssigneeOptions } from '../../mockData';
import { ClientPortalUser, ClientPortalTaskAttachment } from '../../types';
import { useDeliveryApi } from 'infrastructure/api';
import ClientPortalDocumentsContext from '../ClientPortalDocumentsContext';
import { useProjectId } from 'domain/shared/project';
import { getFilePreview } from 'domain/shared/artifact';
import { UploadFileParams } from 'domain/shared/artifact/input/ArtifactUploaderInput/FileUploader/BaseFileUploader';

const ClientPortalTaskForm = ({ clients = [] }: { clients?: ClientPortalUser[] }) => {
  const { getToday } = useDateService();
  const { data, onChangeCallback } = useFormContext();
  const { uploadFile } = useDeliveryApi();
  const projectId = useProjectId();
  const [uploadedAttachments, setUploadedAttachments] = useState<
    (ClientPortalTaskAttachment & { filePreview: string })[]
  >([]);
  const uploadAttachment = async ({ data: fileData, ...params }: UploadFileParams) => {
    const file = fileData.get('file') as File;
    const body = new FormData();
    body.append('file', file);
    const result = await uploadFile({
      ...params,
      url: `/projects/${projectId}/client-portal/task-attachments/temp`,
      data: body,
    });
    const attachment: ClientPortalTaskAttachment = JSON.parse(result.response);
    const filePreview = await getFilePreview(file);
    setUploadedAttachments(previous => [...previous, { ...attachment, filePreview }]);
    return result;
  };
  const assigneeOptions = getClientPortalAssigneeOptions(clients);
  const attachments = [...(data?.attachments || []), ...uploadedAttachments, ...(data?.responseAttachments || [])];
  const responseAttachmentIds = (data?.responseAttachments || []).map((file: ClientPortalTaskAttachment) => file.id);

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12, md: 2 }}>
          <EnumDropdown options={CLIENT_PORTAL_TASK_TYPE_LABELS} source="type" label="Task type" />
        </Grid>
        <Grid container size={12}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ '& .floating-label-input': { mb: 0 } }}>
            <TextInput source="title" label="Title" />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 2 }} sx={{ '& .floating-label-input': { mb: 0 } }}>
            <EnumDropdown options={assigneeOptions} source={'assigneeExternalUserId'} label={'Assignee'} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 2 }} sx={{ '& .floating-label-input': { mb: 0 } }}>
            <DateInput source={'dueDate'} label="Due date" minDate={getToday()} />
          </Grid>
          {data?.type === CLIENT_PORTAL_TASK_TYPES.PROVIDE_INFORMATION && (
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <TextInput
                source="responseLink"
                label="Secure link"
                tooltip={
                  'Provide a secure link for 1Password here. It will be visible to the client in the task details.'
                }
              />
            </Grid>
          )}
        </Grid>
        <Grid size={12}>
          <WysiwygInput source="description" label="Description:" />
        </Grid>
        <Grid size={12}>
          <ClientPortalDocumentsContext projectId={projectId} taskId={data?.id} attachments={attachments}>
            <AttachmentUploaderInput
              label="Attachments"
              source="attachmentIds"
              isNewDocumentStorage
              uploadFile={uploadAttachment}
              fileExtensions="pdf,doc,docx,xls,xlsx,csv,ppt,pptx,txt,rtf,odt,ods,png,jpg,jpeg,zip,mp3,aac,mp4"
              value={data?.id ? [...(data?.attachmentIds || []), ...responseAttachmentIds] : undefined}
              removeArtifactFromContext={(id: string) => {
                if (!responseAttachmentIds.includes(id)) {
                  onChangeCallback?.({
                    attachmentIds: (data?.attachmentIds || []).filter((fileId: string) => fileId !== id),
                  });
                }
              }}
            />
          </ClientPortalDocumentsContext>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ClientPortalTaskForm;
